import type { AntennaType } from '@/types/AntennaType';
import type { ReaderType } from '@/types/ReaderType';
import type { ResponseErrorType } from '@/types/ResponseErrorType';
import type {
    HookType, HookDbType, HookHttpType,
} from '@/types/HookType';
import { HookTypeEnum } from '@/types/enums/HookTypeEnum';
import type {
    WarehouseNodeType, GroupNodeType, LeafNodeType,
} from '@/types/WarehouseNodeType';
import type {
    ProductEanDifferenceValueType,
    SimpleFieldComparisonType,
    MultiLanguageFieldComparisonType,
} from '@/types/ProductEanComparisonType';
import { DiscountTypeEnum } from '@/types/enums/DiscountTypeEnum';
import type { PromotionType } from '@/types/PromotionType';
import type {
    PaymentAdapterConfigType,
    TestPaymentConfigType,
    CsobTerminalConfigType,
    KbTerminalConfigType,
    StripeTerminalConfigType,
} from '@/types/PaymentConfigurationType';
import { PaymentAdapterTypeEnum } from '@/types/enums/PaymentAdapterTypeEnum';
import type {
    ErosAdapterConfigType,
    TestErosConfigType,
    CzechErosConfigType,
    SlovakErosConfigType,
} from '@/types/ErosConfigurationType';
import { ErosAdapterTypeEnum } from '@/types/enums/ErosAdapterTypeEnum';
import type { ProductEpcType } from '@/types/ProductEpcType';
import { LocationTypeEnum } from '@/types/enums/LocationTypeEnum';
import type { LocationType, LocationStoreType } from '@/types/LocationType';

export function isResponseErrorType(error: any): error is ResponseErrorType {
    return error.status !== undefined;
}

export function isReaderType(data: unknown): data is ReaderType {
    return !!data && typeof data === 'object' && 'antennas' in data && Array.isArray((data as ReaderType).antennas);
}

export function isAntennaType(data: unknown): data is AntennaType {
    return !!data && typeof data === 'object' && 'uuid' in data;
}

export function isDbHook(hook: HookType): hook is HookDbType {
    return hook.type === HookTypeEnum.DB;
}

export function isHttpHook(hook: HookType): hook is HookHttpType {
    return hook.type === HookTypeEnum.HTTP;
}

export function isLeafNode(node: WarehouseNodeType): node is LeafNodeType {
    return node.is_leaf === true;
}

export function isGroupNode(node: WarehouseNodeType): node is GroupNodeType {
    return node.is_leaf === false;
}

export function isSimpleField(diff: ProductEanDifferenceValueType): diff is SimpleFieldComparisonType {
    return 'product' in diff && 'ean' in diff;
}

export function isMultiLanguageField(diff: ProductEanDifferenceValueType): diff is MultiLanguageFieldComparisonType {
    if (typeof diff !== 'object' || diff === null) {
        return false;
    }

    return !isSimpleField(diff);
}

export function hasSimpleDiscount(promotion: PromotionType): boolean {
    return promotion.discount_type === DiscountTypeEnum.Simple;
}

export function hasQuantityDiscount(promotion: PromotionType): boolean {
    return promotion.discount_type === DiscountTypeEnum.Quantity;
}

export function isTestPaymentConfig(
    config: PaymentAdapterConfigType,
    type: PaymentAdapterTypeEnum,
): config is TestPaymentConfigType {
    return type === PaymentAdapterTypeEnum.Test;
}

export function isCsobTerminalConfig(
    config: PaymentAdapterConfigType,
    type: PaymentAdapterTypeEnum,
): config is CsobTerminalConfigType {
    return type === PaymentAdapterTypeEnum.CsobTerminal;
}

export function isKbTerminalConfig(
    config: PaymentAdapterConfigType,
    type: PaymentAdapterTypeEnum,
): config is KbTerminalConfigType {
    return type === PaymentAdapterTypeEnum.KbTerminal;
}

export function isStripeTerminalConfig(
    config: PaymentAdapterConfigType,
    type: PaymentAdapterTypeEnum,
): config is StripeTerminalConfigType {
    return type === PaymentAdapterTypeEnum.StripeTerminal;
}

export function isTestErosConfig(
    config: ErosAdapterConfigType,
    type: ErosAdapterTypeEnum,
): config is TestErosConfigType {
    return type === ErosAdapterTypeEnum.Test;
}

export function isCzechErosConfig(
    config: ErosAdapterConfigType,
    type: ErosAdapterTypeEnum,
): config is CzechErosConfigType {
    return type === ErosAdapterTypeEnum.CzechEet;
}

export function isSlovakErosConfig(
    config: ErosAdapterConfigType,
    type: ErosAdapterTypeEnum,
): config is SlovakErosConfigType {
    return type === ErosAdapterTypeEnum.SlovakEet;
}

export function isEpcGroup(epcs: ProductEpcType[]): boolean {
    return epcs.length > 1;
}

export function isEpcSingle(epcs: ProductEpcType[]): boolean {
    return epcs.length === 1;
}

export function isStoreLocation(location: LocationType): location is LocationStoreType {
    return location.type === LocationTypeEnum.Store;
}
